import React, { useState, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'

// Context
import { AppContext } from '../../../../../contexts'
import { AmplitudeContext } from '@homebot/shared-util-quartermaster'

// Components
import { If } from '../../../../../components'
import RenterForm from './RenterForm'
import HomeownerForm from './HomeownerForm'

// Helpers
import { snakeCaseKeys } from '../../../../../helpers/format'

// Hooks
import {
  useBuyerSettings,
  useBuyerSettingsMutation,
} from '../../../../../hooks/buyerSettings'
import { useToggleGuestSignup } from '../../../../../hooks/routing'

// Constants
import { INPUT_NAMES, TABS } from '../../../../../constants/display'
// Constants

// Helpers
import validate from './validator'

const SettingsForm = ({ afterSubmit, ...restProps }) => {
  const { focusedSettingsField } = useContext(AppContext)
  const { ampli } = useContext(AmplitudeContext)

  const [values, setValues] = useState({
    [INPUT_NAMES.CURRENT_MONTHLY_COST]: '',
    [INPUT_NAMES.MAX_BUDGET]: '',
    [INPUT_NAMES.DOWN_PAYMENT]: '',
    [INPUT_NAMES.YEARS_IN_HOME]: '',
    [INPUT_NAMES.RENTABLE_NIGHTS]: '',
  })
  const [dirty, setDirty] = useState({
    [INPUT_NAMES.CURRENT_MONTHLY_COST]: false,
    [INPUT_NAMES.MAX_BUDGET]: false,
    [INPUT_NAMES.DOWN_PAYMENT]: false,
    [INPUT_NAMES.YEARS_IN_HOME]: false,
    [INPUT_NAMES.RENTABLE_NIGHTS]: false,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const buyerSettings = useBuyerSettings()
  const [errors, setErrors] = useState({})
  const toggleSignup = useToggleGuestSignup()
  const mutation = useBuyerSettingsMutation()

  const handleAuthedUserSubmit = e => {
    e.preventDefault()
    const errors = validate(values)

    if (!isEmpty(errors)) {
      setErrors(errors)
    } else {
      setIsSubmitting(true)
      const payload = snakeCaseKeys(values)
      mutation.mutate(payload, {
        onSuccess: () => {
          const prequalFieldChanged =
            dirty[INPUT_NAMES.MAX_BUDGET] || dirty[INPUT_NAMES.DOWN_PAYMENT]
          setDirty({})
          ampli?.budgetSaved()
          afterSubmit(prequalFieldChanged)
        },
        onSettled: () => {
          setIsSubmitting(false)
        },
      })
    }
  }

  const handleChange = e => {
    const {
      target: { name, value },
    } = e

    const newValues = {
      ...values,
      [name]: value,
    }

    const updatedDirtyFields = {
      ...dirty,
      [name]: parseInt(value) !== buyerSettings[name],
    }

    setValues(newValues)
    setDirty(updatedDirtyFields)
  }

  const handleGuestFocus = e => {
    const {
      target: { name },
    } = e

    toggleSignup({
      expandModal: 1,
      focusTab: TABS.SETTINGS,
      focusTarget: name,
    })
    e.preventDefault()
    e.target.blur()
  }

  const handleGuestSubmit = () => {
    toggleSignup({
      expandModal: 1,
      focusTab: TABS.SETTINGS,
    })
  }

  // the backend will send back default values for buyerSettings.currentMonthlyCost and buyerSettings.downPayment
  // however, we want to ignore these defaults. We only want to set values here if the changes were made by the user (i.e. buyerSettings.hasUpdatedListingsBotProfile is true)
  // this is a hack we're using because changing the backend to send null values would necessitate going down a rabbit hole of changes
  useEffect(() => {
    if (!mutation.isLoading && buyerSettings.hasUpdatedListingsBotProfile) {
      setValues({
        [INPUT_NAMES.CURRENT_MONTHLY_COST]:
          buyerSettings.currentMonthlyCost ?? '',
        [INPUT_NAMES.MAX_BUDGET]: buyerSettings.maxBudget ?? '',
        [INPUT_NAMES.DOWN_PAYMENT]: buyerSettings.downPayment ?? '',
        [INPUT_NAMES.YEARS_IN_HOME]: buyerSettings.yearsInHome ?? '',
        [INPUT_NAMES.RENTABLE_NIGHTS]: buyerSettings.rentableNights ?? '',
      })
    }
  }, [
    buyerSettings.currentMonthlyCost,
    buyerSettings.clientType,
    buyerSettings.maxBudget,
    buyerSettings.downPayment,
    buyerSettings.yearsInHome,
    buyerSettings.rentableNights,
    buyerSettings.hasUpdatedListingsBotProfile,
    mutation.isLoading,
  ])

  const guestUserProps = {
    ...restProps,
    onSubmit: handleGuestSubmit,
    onChange: handleChange,
    onFocus: handleGuestFocus,
    values,
    errors,
  }

  const authedUserProps = {
    ...restProps,
    onSubmit: handleAuthedUserSubmit,
    onChange: handleChange,
    isSubmitting,
    values,
    errors,
    focusedField: focusedSettingsField,
  }

  return (
    <>
      <If.Guest>
        <RenterForm {...guestUserProps} />
      </If.Guest>
      <If.Rent>
        <RenterForm {...authedUserProps} />
      </If.Rent>
      <If.Own>
        <HomeownerForm {...authedUserProps} />
      </If.Own>
    </>
  )
}

SettingsForm.propTypes = {
  afterSubmit: PropTypes.func,
}

export default SettingsForm
